import * as THREE from 'three';
import type { TimeOfDayVisual } from '../../sim/timeOfDay';
import type { ParkMaterials } from './materials';

/**
 * COL-011B night glow helpers.
 *
 * Halos are additive camera-facing quads batched into one InstancedMesh per prop/attraction,
 * and light pools are one flat additive disc. Both use shared `mats.glow*` / `mats.lightPool`
 * materials whose opacity the scene host drives from the time-of-day look, so during the day
 * they cost nothing (material.visible = false) and at night the whole park lights up with a
 * handful of extra draw calls.
 */

/** Fixed isometric camera direction (see sceneHost CAMERA_AZIMUTH / CAMERA_ELEVATION). */
const CAMERA_AZIMUTH = THREE.MathUtils.degToRad(41);
const CAMERA_ELEVATION = THREE.MathUtils.degToRad(37);
const CAMERA_DIRECTION = new THREE.Vector3(
  Math.sin(CAMERA_AZIMUTH) * Math.cos(CAMERA_ELEVATION),
  Math.sin(CAMERA_ELEVATION),
  Math.cos(CAMERA_AZIMUTH) * Math.cos(CAMERA_ELEVATION),
).normalize();

/** Rotation that turns a +Z-facing PlaneGeometry toward the fixed camera. */
export const GLOW_FACING = new THREE.Quaternion().setFromRotationMatrix(
  new THREE.Matrix4().lookAt(CAMERA_DIRECTION, new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 1, 0)),
);

export interface GlowSpot {
  position: [number, number, number];
  /** Quad edge length in local units. */
  size: number;
  /** Lay the quad flat on the ground (light pool) instead of facing the camera. */
  flat?: boolean;
}

const GLOW_QUAD = new THREE.PlaneGeometry(1, 1);
const FLAT_FACING = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 2);
const scratch = new THREE.Object3D();
const yawInverse = new THREE.Quaternion();
const yawAxis = new THREE.Vector3(0, 1, 0);

/** A halo above a ground pool — the standard two-quad signature of one warm light source. */
export function lampGlowSpots(x: number, y: number, z: number, haloSize: number, poolRadius: number, groundY = 0): GlowSpot[] {
  return [
    { position: [x, y, z], size: haloSize },
    { position: [x, groundY, z], size: poolRadius * 2, flat: true },
  ];
}

/**
 * One InstancedMesh of camera-facing halos. Because the whole attraction may be yawed by the
 * placement rotation, `orientGlows(worldYaw)` is exposed on `userData` so the parent factory
 * can re-face the quads after applying the entity rotation.
 */
export function createGlowLayer(material: THREE.Material, spots: GlowSpot[]): THREE.InstancedMesh {
  const layer = new THREE.InstancedMesh(GLOW_QUAD, material, spots.length);
  layer.castShadow = false;
  layer.receiveShadow = false;
  layer.frustumCulled = false;
  layer.renderOrder = 20;
  const orient = (worldYaw: number) => {
    yawInverse.setFromAxisAngle(yawAxis, -worldYaw);
    spots.forEach((spot, index) => {
      scratch.position.set(...spot.position);
      if (spot.flat) {
        scratch.quaternion.copy(FLAT_FACING);
      } else {
        scratch.quaternion.copy(yawInverse).multiply(GLOW_FACING);
      }
      scratch.scale.setScalar(spot.size);
      scratch.updateMatrix();
      layer.setMatrixAt(index, scratch.matrix);
    });
    layer.instanceMatrix.needsUpdate = true;
  };
  orient(0);
  layer.userData.orientGlows = orient;
  layer.userData.glowLayer = true;
  return layer;
}

/** Flat additive disc laid on the ground — the warm spill under a lamp or a lit attraction. */
export function createLightPool(material: THREE.Material, radius: number, y: number, x = 0, z = 0): THREE.Mesh {
  const pool = new THREE.Mesh(GLOW_QUAD, material);
  pool.rotation.x = -Math.PI / 2;
  pool.position.set(x, y, z);
  pool.scale.setScalar(radius * 2);
  pool.castShadow = false;
  pool.receiveShadow = false;
  pool.renderOrder = 19;
  pool.userData.lightPool = true;
  return pool;
}

/** Re-faces every glow layer beneath `root` for the given world yaw. */
export function orientGlowLayers(root: THREE.Object3D, worldYaw: number): void {
  root.traverse((child) => {
    const orient = child.userData?.orientGlows as ((yaw: number) => void) | undefined;
    if (orient) {
      orient(worldYaw);
    }
  });
}

type NightMaterials = Pick<ParkMaterials, 'glowWarm' | 'glowCool' | 'lightPool' | 'lamp' | 'festiveTile'>;

/**
 * Pushes the time-of-day look into the shared night materials. Attraction `lights` channel
 * materials are passed separately because they are created per palette.
 */
export function applyNightLook(
  mats: NightMaterials,
  lightChannels: Iterable<THREE.MeshStandardMaterial>,
  look: TimeOfDayVisual,
): void {
  for (const halo of [mats.glowWarm, mats.glowCool]) {
    halo.opacity = look.glowOpacity;
    halo.visible = look.glowOpacity > 0.01;
  }
  mats.lightPool.opacity = look.poolOpacity;
  mats.lightPool.visible = look.poolOpacity > 0.01;
  mats.lamp.emissiveIntensity = look.lampEmissive;
  mats.festiveTile.emissiveIntensity = look.festiveEmissive;
  for (const lights of lightChannels) {
    lights.emissiveIntensity = look.lightsEmissive;
  }
}
